<script lang="ts">
import { DisplayMode, useDisplayConfigStore } from '@/stores/displayConfig'
import type { Page } from '@/types/contentTypes'
import { ContentCardType } from '@/types/contentTypes'
import { defineComponent, ref, type Ref, type PropType } from 'vue'
import LocationCard from './LocationCard.vue'
import AboutMeCard from './AboutMeCard.vue'

export interface DisplayCard {
  data: any
  title: string
  text: string
  link: string
  linkText: string
  id: string
  mode: string
  imageUrl: string
  imageOpacity: number
  type: ContentCardType
  row?: number
  col?: number
}

export default defineComponent({
  name: 'CardPage',
  components: {
    LocationCard,
    AboutMeCard
  },
  data() {
    return {
      imageBase: 0,
      DisplayMode,
      ContentCardType
    }
  },
  props: {
    pageData: {
      type: null as unknown as PropType<Page>,
      required: true
    }
  },
  setup(props) {
    const display = useDisplayConfigStore()
    const itemRefs = ref<HTMLElement[]>([])
    const cards = props.pageData.cards!.map((card) => {
      if (!card.data) {
        card.data = '{}'
      }
      const data = JSON.parse(card.data)
      return ref({
        title: card.title,
        text: data.text,
        link: data.link,
        linkText: data.linkText,
        id: data.id,
        mode: data.mode,
        imageUrl: data.imageUrl,
        imageOpacity: 0.5,
        type: card.type,
        data: JSON.parse(card.data!)
      }) as Ref<DisplayCard>
    })
    const imageRefs: Ref<HTMLElement | null>[] = []
    for (let i = 0; i < cards.length; i++) {
      cards[i].value.imageOpacity = 0.5
      imageRefs.push(ref<HTMLElement | null>(null))
    }
    return { display, itemRefs, cards, imageRefs }
  },
  methods: {
    dimIt() {
      this.updateImageOpacitiesForAllCards()
      setTimeout(() => {
        this.dimIt()
      }, 100)
    },
    updateImageOpacitiesForAllCards() {
      const index = this.getCurrentCardIndex()
      for (let i = 0; i < this.cards.length; i++) {
        this.cards[i].value.imageOpacity = i == index ? this.percentOverlap(i) : 0
      }
    },
    percentOverlap(index: number): number {
      if (this.itemRefs.length == 0 || !this.itemRefs[index]) {
        return 0
      }
      const item = this.itemRefs[index] as HTMLElement
      const img = item.querySelector('img')

      const rect1 = img?.getBoundingClientRect()
      const rect2 = item.getBoundingClientRect()

      if (index == this.cards.length - 1) {
        if (rect1 && rect2) {
          const y_overlap = Math.max(
            0,
            Math.min(rect1.bottom, rect2.bottom) - Math.max(rect1.top, rect2.top)
          )
          return 1 - y_overlap / rect1.height
        }
      } else {
        const nextItem = this.itemRefs[index + 1] as HTMLElement
        const rect3 = nextItem.getBoundingClientRect()

        if (rect1 && rect2 && rect3) {
          const y_overlap = Math.max(
            0,
            Math.min(rect1.bottom, rect3.bottom) - Math.max(rect1.top, rect3.top),
            Math.min(rect1.bottom, rect2.bottom) - Math.max(rect1.top, rect2.top)
          )
          return 1 - y_overlap / rect1.height
        }
      }

      return 0
    },
    getCurrentCardIndex(): number {
      if (this.itemRefs.length == 0) {
        return 0
      }

      const cardRects = this.itemRefs.map((item) => {
        if (!item) {
          return null
        }
        return item.getBoundingClientRect()
      })
      const imageRects = this.itemRefs.map((item) => {
        if (!item) {
          return null
        }
        return item.querySelector('img')!.getBoundingClientRect()
      })

      // the current card is the one one the page with it's top over it's images top
      for (let i = 0; i < this.cards.length - 1; i++) {
        const cardRect = cardRects[i]!
        const imageRect = imageRects[i]!
        const nextCardRect = cardRects[i + 1]!
        const nextImageRect = imageRects[i + 1]!

        // if an item is fully on the page, select it
        if (cardRect.y > 0) {
          return i
        }

        // if the next item hasn't overtaken it's own image, keep the current item selected
        if (nextCardRect.top > nextImageRect.top) {
          return i
        }

        // if the next item has overtaken the bottom of the current image, select the next item
        if (nextCardRect.bottom > imageRect.bottom) {
          return i + 1
        }
      }

      return this.cards.length - 1
    },
    formatImageUrl(imageUrl: string): string {
      return imageUrl.startsWith('..') ? '/src/' + imageUrl.substring(3) : 'api/Content/' + imageUrl
    },
    cardsForRow(col: number, row: number): Ref<DisplayCard>[] {
      return this.cards.filter((c, _) => c.value.row == row && c.value.col == col)
    },
    assignCardRows() {
      var cols = this.columns
      var colCtr = 0
      var dividerOffset = 0
      for (let i = 0; i < this.cards.length; i++) {
        this.cards[i].value.row = dividerOffset
        this.cards[i].value.col = colCtr % cols
        colCtr++
        if (this.cards[i].value.type == ContentCardType.Divider) {
          dividerOffset++
          colCtr = 0
        }
      }
    },
    dividerForRow(row: number): number | undefined {
      // get the nth divider's index
      const dividers = this.cards
        .map((c, i) => (c.value.type == ContentCardType.Divider ? i : -1))
        .filter((i) => i != -1)
      return dividers[row]
    }
  },
  computed: {
    columns() {
      return this.display.displayMode == DisplayMode.FullDesktop || this.display.isKiosk ? 3 : 2
    },
    columnsRange() {
      return Array.from({ length: this.columns }, (_, i) => i)
    },
    // this is the number of v-rows that we need, which is always 1 + the number of dividers
    rowsRange() {
      return Array.from(
        { length: 1 + this.cards.filter((c) => c.value.type == ContentCardType.Divider).length },
        (_, i) => i
      )
    }
  },
  async mounted() {
    this.assignCardRows()

    // ensure the image doesn't bounce on mobile with the address bar appearing and disappearing
    this.imageBase = window.innerHeight * 0.3

    // set up the dimming effect
    this.dimIt()
  },
  watch: {
    // watch the display mode and update the layout accordingly
    'display.displayMode': {
      immediate: true,
      handler() {
        this.assignCardRows()
      }
    }
  }
})
</script>

<template>
  <div v-if="display.displayMode == DisplayMode.Mobile">
    <div v-for="(card, index) in cards" :key="card.value.title" ref="itemRefs">
      <v-card
        class="mobile-card"
        v-bind:class="
          card.value.title == cards[cards.length - 1].value.title ? 'last-mobile-card' : ''
        "
      >
        <template v-if="card.value.type == ContentCardType.StandardCard">
          <v-card-title>{{ card.value.title }}</v-card-title>
          <v-divider thickness="2px" length="100%"></v-divider>
          <v-card-text v-text="card.value.text"></v-card-text>
        </template>
        <template v-else-if="card.value.type == ContentCardType.RawHtmlCard">
          <v-card-title>{{ card.value.title }}</v-card-title>
          <v-divider thickness="2px" length="100%"></v-divider>
          <v-card-text v-html="card.value.text" class="raw-html"></v-card-text>
        </template>
        <template v-else-if="card.value.type == ContentCardType.ActionCard">
          <v-card-title>{{ card.value.title }}</v-card-title>
          <v-divider thickness="2px" length="100%"></v-divider>
          <v-card-text v-text="card.value.text"></v-card-text>
          <v-card-actions>
            <v-btn :href="card.value.link" color="black" block>{{ card.value.linkText }}</v-btn>
          </v-card-actions>
        </template>
        <template v-else-if="card.value.type == ContentCardType.LocationCard">
          <LocationCard :id="card.value.id" :mode="card.value.mode" />
        </template>
        <template v-else-if="card.value.type == ContentCardType.AboutMeCard">
          <AboutMeCard :data="card.value.data" :name="card.value.title" />
        </template>
        <template v-else-if="card.value.type == ContentCardType.Divider">
          <!-- Do nothing for now -->
        </template>
      </v-card>

      <div
        id="imageContainer"
        v-bind:style="'opacity: ' + card.value.imageOpacity + '; bottom: ' + imageBase + 'px'"
      >
        <img :src="formatImageUrl(card.value.imageUrl)" alt="" />
      </div>
    </div>
  </div>
  <div v-else>
    <v-row v-for="r in rowsRange">
      <template v-for="x in columnsRange">
        <v-col class="column" :cols="12 / columns" style="display: flex; flex-direction: column">
          <template v-for="card in cardsForRow(x, r)">
            <v-card v-if="card.value.type !== ContentCardType.Divider" class="desktop-card">
              <template v-if="card.value.type == ContentCardType.StandardCard">
                <v-card-title>{{ card.value.title }}</v-card-title>
                <v-divider thickness="2px" length="100%"></v-divider>
                <v-card-text v-text="card.value.text"></v-card-text>
                <div style="display: flex; justify-content: center">
                  <img :src="formatImageUrl(card.value.imageUrl)" alt="" />
                </div>
              </template>
              <template v-else-if="card.value.type == ContentCardType.RawHtmlCard">
                <v-card-title>{{ card.value.title }}</v-card-title>
                <v-divider thickness="2px" length="100%"></v-divider>
                <v-card-text v-html="card.value.text" class="raw-html"></v-card-text>
                <!-- <div v-if="card.value.imageUrl" style="display: flex; justify-content: center">
                <img :src="formatImageUrl(card.value.imageUrl)" alt="" />
              </div> -->
              </template>
              <template v-else-if="card.value.type == ContentCardType.ActionCard">
                <v-card-title>{{ card.value.title }}</v-card-title>
                <v-divider thickness="2px" length="100%"></v-divider>
                <v-card-text v-text="card.value.text"></v-card-text>
                <v-card-actions>
                  <v-btn :href="card.value.link" color="black" block>{{
                    card.value.linkText
                  }}</v-btn>
                </v-card-actions>
              </template>
              <template v-else-if="card.value.type == ContentCardType.LocationCard">
                <LocationCard :id="card.value.id" :mode="card.value.mode" />
              </template>
              <template v-else-if="card.value.type == ContentCardType.AboutMeCard">
                <AboutMeCard :data="card.value.data" :name="card.value.title" />
              </template>
            </v-card>
          </template>
        </v-col>
      </template>
      <!-- <v-col v-for="card in cards" :key="card.value.title" lg="4" md="6" sm="6">
        <v-card class="desktop-card">
          <v-card-title>{{ card.value.title }}</v-card-title>
          <v-divider thickness="2px" length="100%"></v-divider>
          <v-card-text v-text="card.value.plainText"></v-card-text>
          <div style="display: flex; justify-content: center">
            <img :src="formatImageUrl(card.value.imageUrl)" alt="" />
          </div>
        </v-card>
      </v-col> -->
      <template v-if="dividerForRow(r) !== undefined">
        <v-col class="divider-card" :cols="12">
          <span>{{ cards[dividerForRow(r)!].value.title }}</span>
          <v-divider thickness="5px" length="90%" :color="'cuGold'" :opacity="1"></v-divider>
        </v-col>
      </template>
    </v-row>
  </div>
</template>

<style></style>

<style scoped>
.v-card {
  opacity: 0.85;
}

.mobile-card {
  margin: 10px;
  padding: 10px;
  background-color: #424242;
  color: white;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  border-radius: 10px;
  margin-bottom: 250px;
  z-index: 2;
}

:deep(.v-card-title) {
  font-size: 24px;
  /* text-transform: uppercase; */
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-card .v-divider {
  color: #8f8f8f;
}

.last-mobile-card {
  margin-bottom: 450px;
}

#imageContainer {
  position: fixed;
  display: flex;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 1;
}

#imageContainer img {
  min-width: 250px;
  max-width: 100%;
}

.desktop-card {
  margin: 10px;
  padding: 10px;
  background-color: #424242;
  color: white;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  border-radius: 10px;
  z-index: 2;
  max-width: 450px;
}

.desktop-card .v-card-title {
  font-size: 24px;
}

.desktop-card .v-divider {
  color: #8f8f8f;
}

.desktop-card img {
  border-radius: 10px;
  max-height: 200px;
  max-width: 100%;
}

.v-card-text :deep(table) {
  width: 100%;
}

.v-card-text :deep(table td:nth-child(1)) {
  font-weight: bold;
}

.v-card-text :deep(table td:nth-child(2)) {
  text-align: right;
}

.v-card-actions .v-btn {
  width: 75%;
  color: black;
  background-color: var(--color-cu-gold);
}
</style>

<style lang="css">
.raw-html p {
  &:not(:last-child) {
    margin-bottom: 30px;
  }
}

.raw-html a {
  color: white;
  text-decoration: underline;
}

.divider-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--color-cu-gold);
  font-size: 2.2em;
  letter-spacing: 2px;
  font-weight: 800;
  text-transform: uppercase;
}
</style>
